import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _typeof from "@babel/runtime/helpers/typeof";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/** @jsx jsx */
import { useMemo } from 'react';
import { css, jsx } from '@emotion/react';
import { UNSAFE_BREAKPOINTS_ORDERED_LIST, UNSAFE_buildAboveMediaQueryCSS } from '@atlaskit/primitives/responsive';
import { GRID_COLUMNS } from './config';
// when in doubt simply span all columns
var baseGridItemStyles = css({
  gridColumn: "1 / span ".concat(GRID_COLUMNS)
});
var hideMediaQueries = UNSAFE_buildAboveMediaQueryCSS({
  display: 'none'
});
var gridSpanMediaQueries = UNSAFE_buildAboveMediaQueryCSS(function (breakpoint) {
  return {
    display: 'block',
    // override the display that might be cascaded in from `hideMediaQueries`
    gridColumnEnd: "span var(--grid-item-".concat(breakpoint, "-span, 12)")
  };
});
var gridStartMediaQueries = UNSAFE_buildAboveMediaQueryCSS(function (breakpoint) {
  return {
    gridColumnStart: "var(--grid-item-".concat(breakpoint, "-start, 'auto')")
  };
});

/**
 * Build a set of responsive css variables given a responsive object
 *
 */
function buildCSSVarsFromConfig(_ref) {
  var responsiveObject = _ref.responsiveObject,
    key = _ref.key,
    prefix = _ref.prefix,
    _ref$isValidBreakpoin = _ref.isValidBreakpointValue,
    isValidBreakpointValue = _ref$isValidBreakpoin === void 0 ? function () {
      return true;
    } : _ref$isValidBreakpoin;
  /**
   * This coerces an object of `{ xxs: 12, sm: 'auto', … }` down to `[['xxs', 12], ['sm', 'auto], …]`.  Split out for readability.
   */
  var entries = Object.entries(responsiveObject);
  return entries.reduce(function (acc, _ref2) {
    var _ref3 = _slicedToArray(_ref2, 2),
      breakpoint = _ref3[0],
      value = _ref3[1];
    if (typeof value === 'undefined' || !isValidBreakpointValue(value)) {
      return acc;
    }
    return _objectSpread(_objectSpread({}, acc), {}, _defineProperty({}, "--".concat(prefix, "-").concat(String(breakpoint), "-").concat(key), value));
  }, {});
}

/**
 * __Grid item__
 *
 * A grid item is designed to be nested in a `Grid`. Grid items can span one or many columns.
 *
 * - [Code](https://atlassian.design/components/grid)
 *
 * @example
 * ```jsx
 * import Grid, { GridItem } from '@atlaskit/grid';
 *
 * const App = () => (
 *   <Grid>
 *     <GridItem span="6">half-width content</GridItem>
 *     <GridItem span="6">half-width content</GridItem>
 *   </Grid>
 * );
 * ```
 */
export var GridItem = function GridItem(_ref4) {
  var testId = _ref4.testId,
    children = _ref4.children,
    _ref4$start = _ref4.start,
    startProp = _ref4$start === void 0 ? 'auto' : _ref4$start,
    _ref4$span = _ref4.span,
    spanProp = _ref4$span === void 0 ? 12 : _ref4$span;
  // If `prop` isn't a responsive object, we set the value against the `xs` breakpoint, eg. `span={6}` is the same as `span={{ xxs: 6 }}`
  var span = _typeof(spanProp) === 'object' ? spanProp : {
    xxs: spanProp
  };
  var spanDependencyComparison = JSON.stringify(span); // to compare `span` changes in a `useMemo` deps array (used a few times)
  var spanStyles = useMemo(function () {
    return buildCSSVarsFromConfig({
      responsiveObject: span,
      key: 'span',
      prefix: 'grid-item',
      // We don't want a css var like `--grid-item-xs-span: none` as it's invalid and unused.
      isValidBreakpointValue: function isValidBreakpointValue(value) {
        return value !== 'none';
      }
    });
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `span` will change references easily, but we still need to allow content or key changes to update
  [spanDependencyComparison]);

  // If `prop` isn't a responsive object, we set the value against the `xs` breakpoint, eg. `start={6}` is the same as `start={{ xxs: 6 }}`
  var start = _typeof(startProp) === 'object' ? startProp : {
    xxs: startProp
  };
  var startDependencyComparison = JSON.stringify(start); // to compare `start` changes in a `useMemo` deps array (used a few times)
  var startStyles = useMemo(function () {
    return buildCSSVarsFromConfig({
      responsiveObject: start,
      key: 'start',
      prefix: 'grid-item'
    });
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `start` will change references easily, but we still need to allow content or key changes to update
  [startDependencyComparison]);

  /**
   * Generate all media queries for breakpoints that are available during this render.  This is to avoid rendering media queries for all breakpoints if none are used.
   */
  var mediaQueryStyles = useMemo(function () {
    return UNSAFE_BREAKPOINTS_ORDERED_LIST.reduce(function (acc, breakpoint) {
      var styles = [];
      if (breakpoint in span) {
        if (span[breakpoint] === 'none') {
          styles.push(hideMediaQueries[breakpoint]);
        } else {
          styles.push(gridSpanMediaQueries[breakpoint]);
        }
      }
      if (breakpoint in start) {
        styles.push(gridStartMediaQueries[breakpoint]);
      }
      if (!styles.length) {
        return acc;
      }
      return [].concat(_toConsumableArray(acc), styles);
    }, []);
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `start` and `span` will change references easily, but we still need to allow content or key changes to update.  This _should_ be more performant than running on every render as I don't expect this to change.
  [spanDependencyComparison, startDependencyComparison]);
  return jsx("div", {
    style: _objectSpread(_objectSpread({}, startStyles), spanStyles),
    css: [baseGridItemStyles].concat(_toConsumableArray(mediaQueryStyles)),
    "data-testid": testId
  }, children);
};